import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import TypeResolver from "../type-resolver"
import { url } from "../url"
import { isInternalType } from "../field-formatter"

export default function TypeSelectorDialog({ isOpen, onSelect, onClose }) {
  const [searchQuery, setSearchQuery] = useState("")
  const [allTypes, setAllTypes] = useState([])
  const searchInputRef = useRef(null)
  const { availableTypeNames, refreshTypes, getContentType, types } =
    TypeResolver()

  useEffect(() => {
    if (!isOpen) return
    refreshTypes()
    //focus on the search input
    if (searchInputRef.current) searchInputRef.current.focus()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const typesList = availableTypeNames() // Get all loaded content types
    if (!typesList || !typesList.length) return // Return if no types are available
    //convert tye object to array
    const list = []
    typesList.map((typeName) => {
      const contentType = getContentType(typeName)
      list.push(contentType)
    })

    setAllTypes(list) // Set the types data to state
  }, [types])

  const handleSelectType = (type) => {
    onSelect(type)
    onClose() // Close the dialog after selection
  }

  // Filter types based on the search query and exclude internal types
  const filteredTypes = allTypes?.filter(
    (type) =>
      !isInternalType(type) && // Exclude internal types (ContentType, FieldType, Group, Editor)
      (type?.type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
       type?.label?.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_compact"
      closeOnEscape
      className="w-[600px] h-[80vh] p-6"
    >
      <div className="flex flex-col text-admin_text h-full">
        <div className="flex-shrink-0 px-3">
          <Typography 
           component="h2"
           variant="admin_h4"
            className="text-center mb-4"
          >
            What type of content are you looking for?
          </Typography>

          {/* Search Input */}
          <Input
            variant="admin"
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search"
          />
        </div>

        {/* Displaying filtered content types */}
        <div className="h-[55vh] overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_1 scrollbar-track-admin_paper mt-4">
          {filteredTypes?.length ? (
            filteredTypes.map((type, index) => (
              <div
                key={index}
                className="flex justify-between items-center border-b border-admin_bordered hover:bg-admin_canvas cursor-pointer p-3"
                onClick={() => handleSelectType(type)}
              >
                <div className="flex flex-row items-center">
                  {type.thumbnail ? (
                    <img
                      src={url(type.thumbnail)}
                      alt={`${type.label} thumbnail`}
                      className="w-12 h-12 object-cover mr-4"
                    />
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center bg-admin_neutral_2 rounded-full mr-4">
                      {type.type.charAt(0)}
                    </div>
                  )}

                  <div className="flex flex-col">
                    <Typography 
                      variant="admin_label" 
                      className="mb-0"
                    >
                      {type.type}
                    </Typography>

                    <Typography 
                    variant="admin_txt1_dim"
                    >
                      {type.label}
                    </Typography>
                  </div>
                </div>

                <Button>
                  <ChevronRightIcon className="w-6 h-6" />
                </Button>
              </div>
            ))
          ) : (
            <Typography 
              variant="admin_body_dim"
              className="text-center"
            >
              No content types found.
            </Typography>
          )}
        </div>
      </div>
    </Modal>
  )
}
